import { useState } from 'react';
import * as XLSX from 'xlsx';

// Define the types of files we can parse
type FileType = 'csv' | 'xlsx' | 'xls' | 'unknown';

// Define the structure for contact data based on the template
export interface ParsedContact {
  [key: string]: string | undefined;
  crmID?: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  phoneStatus?: string;
  email?: string;
  source?: string;
  tags?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  birthdate?: string;
}

interface UseFileParserReturn {
  parseFile: (file: File) => Promise<ParsedContact[]>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to parse CSV and Excel files
 * @returns Object containing parse function, loading state, and error
 */
export const useFileParser = (): UseFileParserReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Parse a file based on its extension
   */
  const parseFile = async (file: File): Promise<ParsedContact[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const fileType = getFileType(file);

      if (fileType === 'unknown') {
        throw new Error('Unsupported file type. Please upload a CSV or Excel file.');
      }

      // Parse the file based on its type
      let parsedData: ParsedContact[] = [];

      if (fileType === 'csv') {
        parsedData = await parseCSV(file);
      } else if (fileType === 'xlsx' || fileType === 'xls') {
        parsedData = await parseExcel(file);
      }

      return parsedData;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to parse file');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { parseFile, isLoading, error };
};

/**
 * Determine the file type based on extension
 */
const getFileType = (file: File): FileType => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'csv':
      return 'csv';
    case 'xlsx':
      return 'xlsx';
    case 'xls':
      return 'xls';
    default:
      return 'unknown';
  }
};

/**
 * Parse a CSV file using the exact column names from our template
 */
const parseCSV = async (file: File): Promise<ParsedContact[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const csv = event.target?.result as string;
        if (!csv) {
          reject(new Error('Failed to read CSV file'));
          return;
        }

        // Split the CSV into lines
        const lines = csv.split('\n');

        // Get the headers from the first line
        const headers = lines[0].split(',').map((header) => header.trim());

        // Parse each line into an object
        const data: ParsedContact[] = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // Handle quoted values with commas inside
          const values: string[] = [];
          let inQuote = false;
          let currentValue = '';

          for (let j = 0; j < line.length; j++) {
            const char = line[j];

            if (char === '"' && (j === 0 || line[j - 1] !== '\\')) {
              inQuote = !inQuote;
            } else if (char === ',' && !inQuote) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }

          // Add the last value
          values.push(currentValue.trim());

          // Create contact object with exact column names from template
          const contact: Partial<ParsedContact> = {
            firstName: '',
            lastName: '',
          };

          headers.forEach((header, index) => {
            const value = values[index]?.trim() || '';
            // Remove quotes if present
            const cleanValue = value.replace(/^"(.*)"$/, '$1');

            // Map each header to the corresponding field in our ParsedContact interface
            switch (header) {
              case 'crmID':
                contact.crmID = cleanValue;
                break;
              case 'firstName':
                contact.firstName = cleanValue;
                break;
              case 'lastName':
                contact.lastName = cleanValue;
                break;
              case 'phoneNumber':
                contact.phoneNumber = cleanValue;
                break;
              case 'phoneStatus':
                contact.phoneStatus = cleanValue;
                break;
              case 'email':
                contact.email = cleanValue;
                break;
              case 'source':
                contact.source = cleanValue;
                break;
              case 'tags':
                contact.tags = cleanValue;
                break;
              case 'street':
                contact.street = cleanValue;
                break;
              case 'city':
                contact.city = cleanValue;
                break;
              case 'state':
                contact.state = cleanValue;
                break;
              case 'zip':
                contact.zip = cleanValue;
                break;
              case 'birthdate':
                contact.birthdate = cleanValue;
                break;
            }
          });

          // Ensure required fields are present
          if (!contact.firstName) contact.firstName = '';
          if (!contact.lastName) contact.lastName = '';

          // Include all contacts in the parsed data, even if some fields are empty
          data.push(contact as ParsedContact);
        }

        resolve(data);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read CSV file'));
    };

    reader.readAsText(file);
  });
};

/**
 * Parse an Excel file using the xlsx library with exact column names from template
 */
const parseExcel = async (file: File): Promise<ParsedContact[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        if (!data) {
          reject(new Error('Failed to read Excel file'));
          return;
        }

        // Parse the Excel file using xlsx
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert the worksheet to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          reject(new Error('Excel file must contain at least a header row and one data row'));
          return;
        }

        // Get the headers from the first row
        const headers = (jsonData[0] as string[]).map((header) => String(header).trim());

        // Parse each row into a contact object
        const contacts: ParsedContact[] = [];

        // Skip the header row (index 0)
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];
          if (!row || row.length === 0) continue;

          // Create contact object with exact column names from template
          const contact: Partial<ParsedContact> = {
            firstName: '',
            lastName: '',
          };

          headers.forEach((header, index) => {
            const value = row[index] !== undefined ? String(row[index]).trim() : '';

            // Map each header to the corresponding field in our ParsedContact interface
            switch (header) {
              case 'crmID':
                contact.crmID = value;
                break;
              case 'firstName':
                contact.firstName = value;
                break;
              case 'lastName':
                contact.lastName = value;
                break;
              case 'phoneNumber':
                contact.phoneNumber = value;
                break;
              case 'phoneStatus':
                contact.phoneStatus = value;
                break;
              case 'email':
                contact.email = value;
                break;
              case 'source':
                contact.source = value;
                break;
              case 'tags':
                contact.tags = value;
                break;
              case 'street':
                contact.street = value;
                break;
              case 'city':
                contact.city = value;
                break;
              case 'state':
                contact.state = value;
                break;
              case 'zip':
                contact.zip = value;
                break;
              case 'birthdate':
                contact.birthdate = value;
                break;
            }
          });

          // Ensure required fields are present
          if (!contact.firstName) contact.firstName = '';
          if (!contact.lastName) contact.lastName = '';

          // Include all contacts in the parsed data, even if some fields are empty
          contacts.push(contact as ParsedContact);
        }

        resolve(contacts);
      } catch (err) {
        console.error('Error parsing Excel:', err);
        reject(err instanceof Error ? err : new Error('Failed to parse Excel file'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };

    // Read the file as an array buffer
    reader.readAsArrayBuffer(file);
  });
};
